import { useTranslation } from 'next-i18next';
import React from 'react';

import { useAppSelector } from '@/redux';
import { NS } from '@/constants';

import styles from './MillesimaPrice.module.scss';
import { HTML_ENTITY } from '@/constants/htmlEntity';
import { PRICE } from '@/constants/price';

type MillesimaPriceVat = {
  display: boolean;
  included: boolean;
}

type MillesimaPriceUnit = {
  display: boolean;
  type: 'unit' | 'liter';
}

export type MillesimaPriceClassName = {
  container?: string;
  value?: string;
  vat?: string;
  unit?: string;
  bold?: string;
}

export type MillesimaPriceProps = {
  value?: string | number;
  vat?: MillesimaPriceVat;
  unit?: MillesimaPriceUnit;
  strikeout?: boolean;
  className?: MillesimaPriceClassName;
  isInteger?: boolean;
  displayHt?: boolean;
  isAdditionnalFees?: boolean;
  localePrice?: string;
  currencyISOPrice?: string;
}

const DEFAULT_VAT: MillesimaPriceVat = {
  display: false,
  included: false
};

const DEFAULT_UNIT: MillesimaPriceUnit = {
  display: false,
  type: 'unit'
};

const DEFAULT_CLASSNAME: MillesimaPriceClassName = {
  container: undefined,
  value: undefined,
  vat: undefined,
  unit: undefined
};

export const MillesimaPrice = ({
  value = 0,
  vat = DEFAULT_VAT,
  unit = DEFAULT_UNIT,
  strikeout = false,
  className = DEFAULT_CLASSNAME,
  isInteger = false,
  displayHt = false,
  isAdditionnalFees = false,
  localePrice = null,
  currencyISOPrice = null
}: MillesimaPriceProps): React.JSX.Element => {
  const { t } = useTranslation(NS.COMMON);
  const { locale, currencyISO: currency } = useAppSelector(({ root }) => root.storeConf);

  const price = (
    <span className={className.value} data-test-id="price">
      {value === PRICE.FREE ? t('product.free') : MillesimaPrice.format(t, {
        value: value as number,
        locale,
        currency,
        isInteger,
        isAdditionnalFees,
        localePrice,
        currencyISOPrice
      })}
    </span>
  );

  const vatBloc = vat.display && (
    <span className={className.vat} data-test-id="vat">
      {HTML_ENTITY.NO_BREAK_SPACE}{t(vat.included ? 'product.withTax' : 'product.withoutTax')}
    </span>
  );

  const unitBloc = unit.display && (
    <span className={className.unit} data-test-id="unit">
      {HTML_ENTITY.NO_BREAK_SPACE}/{HTML_ENTITY.NO_BREAK_SPACE}{unit.type === 'unit' ? t('product.unit') : 'L'}
    </span>
  );

  const htBloc = displayHt && (
    <span className={className.unit} data-test-id="ht-price">
      {HTML_ENTITY.NO_BREAK_SPACE}{t('product.withoutTax')}
    </span>
  );

  const containerClassName = [
    className.container,
    className.bold,
    strikeout && styles.strikeout
  ].filter(Boolean).join(' ');

  return (
    <div
      translate="no"
      data-test-id="millesima-price"
      data-rbf="millesima-price"
      className={containerClassName}
    >
      {price}{vatBloc}{unitBloc}{htBloc}
    </div>
  );
};

MillesimaPrice.format = (t,
  {
    value = 0,
    locale = 'fr-FR',
    currency = 'EUR',
    isInteger = false,
    isAdditionnalFees = false,
    localePrice = null,
    currencyISOPrice = null
  }
) => {
  const digits = isInteger ? 0 : 2;
  const formatParams = {
    value: {
      currency: currencyISOPrice || currency,
      locale: localePrice || locale,
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
    },
  };

  const translationKey = isAdditionnalFees ? 'product.formattedAdditionnalFees' : 'product.formattedPrice';

  return t(translationKey, { value, formatParams });
};

export default MillesimaPrice;
